<script setup lang="ts">
    import Alert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { setupTags } from '@shared/components/Tags/Tags';
    import type ContactFilter from './ContactFilter.vue';
    import type { Category } from '../../Contact/ContactTypes';

    const props = defineProps<{
        filterRef: InstanceType<typeof ContactFilter> | undefined;
        totalCount: number;
    }>();

    const tags = setupTags();

    function getTagNames(tagIds: string[]): string {
        return tagIds.map((id) => tags.state.list.find((t) => t.TagId === id)?.Title ?? id).join(', ');
    }

    function getCategoryNames(catIds: string[]): string {
        const catList: Category[] = props.filterRef?.categories ?? [];
        return catIds.map((id) => catList.find((c) => c.CategoryId === id)?.Name ?? id).join(', ');
    }
</script>

<template>
    <Alert :show="filterRef && filterRef.filterCount > 0" icon="filter" :type="AlertTypes.Info" :dismissible="false">
        <template #content>
            <strong>{{ totalCount }}</strong>
            {{ $Resources.ActiveContactFiltersHeading }}. {{ $Resources.ActiveContactFiltersText }}:
            <ul class="m-xs-t m-xs-b">
                <li v-for="(f, i) in filterRef?.addedFilters ?? []" :key="'f-' + i" class="m-xs-b">
                    {{ $Resources.ActiveContactFiltersChoosenField }}
                    <strong>{{ f.fieldDisplayName }}</strong>
                    <span class="text-lowercase m-xxs-l">{{ f.operatorName + ' ' }}</span>
                    <strong v-if="filterRef?.getFilterValueDisplay(f)">"{{ filterRef?.getFilterValueDisplay(f) }}"</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearFilter(i)">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                    <div v-if="i < (filterRef?.addedFilters?.length ?? 0) - 1">
                        <small>{{ filterRef?.andOrOperator === 'and' ? $Resources.OperatorAndLabel : $Resources.OperatorOrLabel }}</small>
                    </div>
                </li>
                <li v-if="filterRef?.statusIsBlocked !== null" class="m-xs-b">
                    {{ $Resources.ContactFilterIsBlockedLabel }}:
                    <strong>{{ filterRef?.statusIsBlocked ? $Resources.FilterActiveLabel : $Resources.NoFilterActiveLabel }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearStatusIsBlocked()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
                <li v-if="filterRef?.statusIsSmsBlocked !== null" class="m-xs-b">
                    {{ $Resources.ContactFilterIsSmsBlockedLabel }}:
                    <strong>{{ filterRef?.statusIsSmsBlocked ? $Resources.FilterActiveLabel : $Resources.NoFilterActiveLabel }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearStatusIsSmsBlocked()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
                <li v-if="filterRef?.statusHasBounced !== null" class="m-xs-b">
                    {{ $Resources.ContactFilterHasBouncedLabel }}:
                    <strong>{{ filterRef?.statusHasBounced ? $Resources.FilterActiveLabel : $Resources.NoFilterActiveLabel }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearStatusHasBounced()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
                <li v-if="filterRef?.statusHasSmsBounced !== null" class="m-xs-b">
                    {{ $Resources.ContactFilterHasSmsBouncedLabel }}:
                    <strong>{{ filterRef?.statusHasSmsBounced ? $Resources.FilterActiveLabel : $Resources.NoFilterActiveLabel }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearStatusHasSmsBounced()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
                <li v-if="tags.state.selectedIds.length > 0" class="m-xs-b">
                    {{ $Resources.ContactListFilteredByTagsMessage }}:
                    <strong>{{ getTagNames(tags.state.selectedIds ?? []) }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="tags.clear()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
                <li v-if="(filterRef?.missingTags?.length ?? 0) > 0" class="m-xs-b">
                    {{ $Resources.ContactListFilteredByMissingTagsMessage }}:
                    <strong>{{ getTagNames(filterRef?.missingTags ?? []) }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearMissingTags()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
                <li v-if="(filterRef?.activeCategories?.length ?? 0) > 0" class="m-xs-b">
                    {{ $Resources.ContactListFilteredByActiveCategoriesMessage }}:
                    <strong>{{ getCategoryNames(filterRef?.activeCategories ?? []) }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearActiveCategories()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
                <li v-if="(filterRef?.blockedCategories?.length ?? 0) > 0" class="m-xs-b">
                    {{ $Resources.ContactListFilteredByBlockedCategoriesMessage }}:
                    <strong>{{ getCategoryNames(filterRef?.blockedCategories ?? []) }}</strong>
                    <button class="btn btn-info btn-xxs m-xs-l" @click="filterRef?.clearBlockedCategories()">
                        {{ $Resources.ActiveContactFiltersClearBtnText }}
                    </button>
                </li>
            </ul>
            <button v-if="(filterRef?.filterCount ?? 0) > 1" class="btn btn-info btn-sm" @click="filterRef?.clearAllFilters()">
                {{ $Resources.ActiveContactFiltersClearAllBtnText }}
            </button>
        </template>
    </Alert>
</template>
